import { Button, IconButton } from '@ads/components-react';
import {
  ArrowsPointingOutIcon,
  CheckIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDurationLabel } from '../../../utils/bannerMedia';
import { formatBannerDate } from '../../../utils/formatBannerDate';
import { BannerPreviewDialog } from '../../molecules/BannerPreviewDialog';
import { BannerPreviewFrame } from '../../molecules/BannerPreviewFrame';

export interface BannerPreviewPanelProps {
  readonly imageUrl: string | undefined;
  readonly videoUrl: string | undefined;
  /** Título exibido sobre a mídia; `undefined` quando "Exibir título" está desmarcado. */
  readonly overlayTitle: string | undefined;
  /** Rótulo do idioma selecionado na barra de idioma. */
  readonly languageLabel: string;
  readonly isVideoMode: boolean;
  /** Duração do vídeo escolhido, em segundos; `null` quando não há vídeo. */
  readonly videoDurationSeconds: number | null;
  /** Duração informada manualmente, em segundos. */
  readonly durationSeconds: number;
  readonly startDate: string;
  readonly endDate: string;
  readonly url: string;
  readonly isGeneral: boolean;
  readonly classStudentCount: number;
}

/** Linha de metadado do preview. */
function MetaRow({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="flex items-start justify-between gap-mini">
      <span className="ts-caption-xxs text-ctx-content-base">{label}</span>
      <span className="ts-subtitle-xxs text-right text-ctx-content-title">{value}</span>
    </div>
  );
}

/**
 * Painel de preview do formulário de Banner (coluna fixa à direita).
 *
 * Reflete em tempo real a mídia, o título sobreposto, o idioma e os metadados de
 * publicação — tipo de mídia, formato, exibição, tempo em tela, período e destino
 * do clique. O vídeo é reproduzido sozinho, sem som e em loop — sem player nem
 * controles, exatamente como o aluno vê; sem mídia, exibe o estado vazio. O botão "Ver em tela cheia" abre o mesmo banner nas duas
 * larguras do container real (205px e 358px).
 */
export function BannerPreviewPanel({
  imageUrl,
  videoUrl,
  overlayTitle,
  languageLabel,
  isVideoMode,
  videoDurationSeconds,
  durationSeconds,
  startDate,
  endDate,
  url,
  isGeneral,
  classStudentCount,
}: BannerPreviewPanelProps) {
  const { t } = useTranslation('banner');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  // Resolução real da mídia, reportada pelo frame quando o arquivo carrega — a
  // linha "Formato" mostra o que veio no arquivo, não a especificação de projeto.
  const [mediaSize, setMediaSize] = useState<{ width: number; height: number } | null>(null);
  const [isLinkCopied, setIsLinkCopied] = useState(false);

  const hasVideo = videoUrl !== undefined;

  const trimmedUrl = url.trim();

  /**
   * Copia o destino do clique. O rótulo do botão vira "Link copiado" por dois
   * segundos — é o único retorno visível, já que o painel não emite toast.
   */
  const copyLink = async () => {
    if (!trimmedUrl) return;
    try {
      await navigator.clipboard.writeText(trimmedUrl);
      setIsLinkCopied(true);
      setTimeout(() => setIsLinkCopied(false), 2000);
    } catch {
      // Sem permissão de clipboard não há o que fazer além de manter o link à mostra.
    }
  };

  const totalSeconds = videoDurationSeconds ?? 0;

  const mediaTypeLabel = isVideoMode
    ? hasVideo
      ? t('banner.form.preview.mediaTypeVideo')
      : t('banner.form.preview.mediaTypeVideoPending')
    : t('banner.form.preview.mediaTypeImage');

  const periodLabel =
    startDate || endDate
      ? `${startDate ? formatBannerDate(startDate) : '—'} — ${endDate ? formatBannerDate(endDate) : '—'}`
      : t('banner.form.preview.noPeriod');

  return (
    <aside className="flex w-full flex-col gap-mini self-start rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs shadow-level1 lg:sticky lg:top-xxs lg:max-w-[420px]">
      <div className="flex flex-row flex-wrap justify-between items-end gap-quark">
        <h2 className="ts-heading-xs text-ctx-content-title">{t('banner.form.preview.heading')}</h2>
        <p className="ts-caption-xxs text-ctx-content-base">{t('banner.form.preview.subtitle')}</p>
      </div>

      <BannerPreviewFrame
        imageUrl={imageUrl}
        videoUrl={videoUrl}
        overlayTitle={overlayTitle}
        onMediaLoad={setMediaSize}
      />

      <div className="flex flex-col gap-nano border-t-hairline border-ctx-layout-border pt-micro">
        <MetaRow label={t('banner.form.preview.meta.language')} value={languageLabel} />
        <MetaRow label={t('banner.form.preview.meta.mediaType')} value={mediaTypeLabel} />
        <MetaRow
          label={t('banner.form.preview.meta.format')}
          value={
            mediaSize
              ? `${mediaSize.width} × ${mediaSize.height}`
              : t('banner.form.preview.formatUnknown')
          }
        />
        <MetaRow
          label={t('banner.form.preview.meta.display')}
          value={
            isGeneral
              ? t('banner.form.classStudents.summaryAll')
              : t('banner.form.classStudents.summary', { count: classStudentCount })
          }
        />
        <MetaRow
          label={t('banner.form.preview.meta.screenTime')}
          value={
            hasVideo && totalSeconds > 0
              ? t('banner.form.preview.screenTimeFromVideo', {
                  duration: formatDurationLabel(totalSeconds),
                })
              : t('banner.form.preview.screenTimeSeconds', { count: durationSeconds })
          }
        />
        <MetaRow label={t('banner.form.preview.meta.period')} value={periodLabel} />

        <div className="flex items-center justify-between gap-mini">
          <span className="shrink-0 ts-caption-xxs text-ctx-content-base">
            {t('banner.form.preview.meta.click')}
          </span>

          {trimmedUrl ? (
            <span className="flex min-w-none flex-1 items-center justify-end gap-quark">
              {/* Link longo trunca em vez de esticar o painel; o endereço completo
                  fica no `title` e a cópia resolve o resto. */}
              <a
                href={trimmedUrl}
                target="_blank"
                rel="noreferrer"
                title={trimmedUrl}
                className="truncate ts-subtitle-xxs text-ctx-content-primary underline"
              >
                {trimmedUrl}
              </a>
              <IconButton
                icon={isLinkCopied ? CheckIcon : ClipboardDocumentIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={t(
                  isLinkCopied ? 'banner.form.preview.linkCopied' : 'banner.form.preview.copyLink',
                )}
                onClick={() => void copyLink()}
              />
            </span>
          ) : (
            <span className="ts-subtitle-xxs text-right text-ctx-content-title">
              {t('banner.form.preview.noLink')}
            </span>
          )}
        </div>
      </div>

      <Button
        type="button"
        hierarchy="secondary"
        size="md"
        fullWidth
        leadingIcon={ArrowsPointingOutIcon}
        onClick={() => setIsDialogOpen(true)}
      >
        {t('banner.form.preview.fullscreen')}
      </Button>

      <BannerPreviewDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        imageUrl={imageUrl}
        videoUrl={videoUrl}
        overlayTitle={overlayTitle}
      />
    </aside>
  );
}
